import { useEffect, useRef, useState } from "react";
import { BadgeCheck, CheckCircle2, Clock, FileUp, Loader2, MailCheck, MessageCircle, Send, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { API, errText } from "@/site/common";
import { field, memberApi } from "@/site/member";
import { card, smallBtn } from "@/site/account/PhotoCard";

const primary = `${smallBtn} bg-orange-500 hover:bg-orange-600 text-white`;
const ghost = `${smallBtn} border border-slate-200 text-slate-700 hover:bg-slate-50`;
const Pill = ({ tone, children, testid }) => <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${tone}`} data-testid={testid}>{children}</span>;
const OK = ({ testid }) => <Pill tone="bg-emerald-50 text-emerald-700" testid={testid}><CheckCircle2 size={13} />Doğrulandı</Pill>;

function Row({ icon: Icon, title, sub, status, children, testid }) {
  return (
    <div className="flex items-start gap-3.5 pt-5 first:pt-0 border-t first:border-0 border-slate-100" data-testid={testid}>
      <span className="grid place-items-center w-10 h-10 rounded-xl bg-slate-100 text-slate-600 shrink-0"><Icon size={18} /></span>
      <div className="flex-1 min-w-0 space-y-2.5"><div className="flex flex-wrap items-center gap-2"><b className="text-slate-900">{title}</b>{status}</div>{sub && <p className="text-sm text-slate-500 break-words">{sub}</p>}{children}</div>
    </div>
  );
}

function EmailRow({ member }) {
  return <Row icon={MailCheck} title="E-posta" sub={member.email} status={member.badges?.email ? <OK testid="verify-email-status" /> : <Pill tone="bg-amber-50 text-amber-700" testid="verify-email-status">Doğrulanmadı</Pill>} testid="verify-email-row" />;
}

const spin = <Loader2 size={16} className="animate-spin" />;

function PhonePending({ req, phone }) {
  return (
    <div className="space-y-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4" data-testid="verify-phone-pending">
      <div className="flex flex-wrap items-baseline gap-3"><span className="text-xs font-bold tracking-wider text-emerald-800">KODUNUZ</span><b className="font-mono text-2xl tracking-[.35em] text-slate-900" data-testid="verify-phone-code">{req.code}</b></div>
      <ol className="list-decimal pl-5 text-sm text-slate-700 space-y-1">
        <li>Aşağıdaki butona basın; WhatsApp'ta kodlu hazır mesaj açılır.</li>
        <li>Mesajı değiştirmeden <b>{phone}</b> numaralı WhatsApp'ınızdan gönderin.</li>
      </ol>
      <a href={req.link} target="_blank" rel="noreferrer" className={`${smallBtn} bg-emerald-600 hover:bg-emerald-700 text-white`} data-testid="verify-phone-open-wa"><MessageCircle size={16} />WhatsApp'ı aç ve gönder</a>
      <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="verify-phone-waiting"><Loader2 size={13} className="animate-spin mt-0.5 shrink-0" />Mesajınız bize ulaşınca bu sayfa kendiliğinden güncellenir. Bilgisayardaysanız kodu telefonunuzdan {req.number} numarasına WhatsApp ile gönderebilirsiniz. Kod 15 dakika geçerlidir.</p>
    </div>
  );
}

function PhoneRow({ member, setMember }) {
  const [req, setReq] = useState(null), [busy, setBusy] = useState(false);
  const ok = member.badges?.phone;
  useEffect(() => {
    if (ok || !member.phone) return undefined;
    let alive = true;
    const poll = async (announce) => {
      try {
        const r = await memberApi.get(`${API}/member/phone/status`);
        if (!alive) return;
        if (r.data.verified) { setMember(r.data.member); setReq(null); if (announce) toast.success("Telefon numaranız WhatsApp ile doğrulandı"); }
        else setReq(r.data.pending ? r.data : null);
      } catch { /* sonraki denemede */ }
    };
    if (!req) { poll(false); return () => { alive = false; }; }
    const t = setInterval(() => poll(true), 4000);
    return () => { alive = false; clearInterval(t); };
  }, [ok, member.phone, req === null]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setBusy(true);
    try { const r = await memberApi.post(`${API}/member/phone/start`); if (r.data.verified) { setMember(r.data.member); toast.success("Telefonunuz zaten doğrulanmış"); } else setReq(r.data); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  return (
    <Row icon={Smartphone} title="Cep telefonu" sub={member.phone || "Profilinizde telefon yok"} status={ok ? <OK testid="verify-phone-status" /> : <Pill tone="bg-amber-50 text-amber-700" testid="verify-phone-status">Doğrulanmadı</Pill>} testid="verify-phone-row">
      {!ok && member.phone && (req ? <PhonePending req={req} phone={member.phone} /> : <>
        <p className="text-sm text-slate-500">Numaranızı doğrulamak için WhatsApp'tan bize 6 haneli bir kod göndermeniz yeterli.</p>
        <button type="button" onClick={start} disabled={busy} className={`${smallBtn} bg-emerald-600 hover:bg-emerald-700 text-white`} data-testid="verify-phone-start">{busy ? spin : <MessageCircle size={16} />}WhatsApp ile doğrula</button>
      </>)}
    </Row>
  );
}

const YETKI = { pending: ["bg-sky-50 text-sky-700", "İnceleniyor", Clock], rejected: ["bg-red-50 text-red-700", "Onaylanmadı", null], none: ["bg-amber-50 text-amber-700", "Belge gönderilmedi", null] };

function YetkiForm({ member, setMember, pending }) {
  const ref = useRef(null), [no, setNo] = useState(member.yetki_no || ""), [file, setFile] = useState(null), [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const fd = new FormData(); fd.append("yetki_no", no); fd.append("file", file);
    try { const r = await memberApi.post(`${API}/member/yetki`, fd); setMember(r.data); setFile(null); ref.current.value = ""; toast.success("Belge numaranız ve belgeniz gönderildi; ekibimiz inceleyip onaylayacak"); } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-testid="verify-yetki-form">
      <label className="block text-sm font-semibold text-slate-700">Yetki Belgesi No<input className={`${field} mt-1.5 sm:w-60 tracking-wider`} inputMode="numeric" maxLength={10} value={no} onChange={(e) => setNo(e.target.value.replace(/\D/g, ""))} placeholder="Örn. 3401234" data-testid="verify-yetki-no" /></label>
      {member.yetki_no && no !== member.yetki_no && <p className="text-xs text-amber-700" data-testid="verify-yetki-no-warning">Numara değişirse yayındaki ilanlarınız yeniden onaya gönderilir.</p>}
      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" hidden onChange={(e) => setFile(e.target.files[0] || null)} data-testid="verify-yetki-input" />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => ref.current.click()} className={ghost} data-testid="verify-yetki-pick"><FileUp size={16} />{file ? "Dosyayı değiştir" : "Belgeyi seç (PDF, JPG, PNG)"}</button>
        {file && <span className="text-sm text-slate-600 truncate max-w-[16rem]" data-testid="verify-yetki-file-name">{file.name}</span>}
      </div>
      <button disabled={busy || !file || !/^\d{6,10}$/.test(no)} className={primary} data-testid="verify-yetki-submit">{busy ? spin : <Send size={16} />}{pending ? "Yeniden gönder" : "İncelemeye gönder"}</button>
    </form>
  );
}

function YetkiRow({ member, setMember }) {
  const st = member.badges?.yetki ? "verified" : member.yetki_status === "verified" ? "none" : member.yetki_status;
  const [open, setOpen] = useState(st !== "pending");
  useEffect(() => setOpen(st !== "pending"), [st]);
  const [tone, label, Icon] = YETKI[st] || YETKI.none;
  return (
    <Row icon={BadgeCheck} title="Taşınmaz Ticareti Yetki Belgesi" sub={st === "verified" || st === "pending" ? `Belge No: ${member.yetki_no}` : "Belge numaranızı yazın ve belgenizi yükleyin; ikisi birlikte incelenir."} status={st === "verified" ? <OK testid="verify-yetki-status" /> : <Pill tone={tone} testid="verify-yetki-status">{Icon && <Icon size={13} />}{label}</Pill>} testid="verify-yetki-row">
      {st === "rejected" && member.yetki_note && <p className="rounded-xl bg-red-50 text-red-700 text-sm px-3.5 py-2.5" data-testid="verify-yetki-note">Neden: {member.yetki_note}</p>}
      {st === "pending" && <p className="text-sm text-slate-500">Belgeniz ekibimizde. Onaylandığında rozet profilinizde görünür.</p>}
      {st === "pending" && !open && <button type="button" onClick={() => setOpen(true)} className={ghost} data-testid="verify-yetki-change">Belgeyi değiştir</button>}
      {st !== "verified" && open && <YetkiForm member={member} setMember={(m) => { setMember(m); setOpen(false); }} pending={st === "pending"} />}
    </Row>
  );
}

export function Verifications({ member, setMember }) {
  return (
    <section className={card} data-testid="member-verifications">
      <div><h2 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck size={18} />Güven rozetleri</h2>
        <p className="mt-1 text-sm text-slate-500">Doğrulanan bilgileriniz profilinizde ve ilanlarınızda rozet olarak görünür; alıcılar size daha çok güvenir.</p></div>
      <EmailRow member={member} />
      <PhoneRow member={member} setMember={setMember} />
      {member.type === "kurumsal" && <YetkiRow member={member} setMember={setMember} />}
    </section>
  );
}
